import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { PAGE_WIDTH, setPageWidth, usePageWidth } from "../typography";

/**
 * Drag handles on both edges of the writing column. The column stays centred, so either
 * handle changes both sides. Widths are in ch of the writing font; double-click resets.
 */
export function MeasureHandles() {
  const width = usePageWidth();
  const frame = useRef<HTMLDivElement>(null);
  const probe = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<"left" | "right" | null>(null);

  /** Widest column that fits the screen, in ch. */
  const fit = () => {
    const ch = probe.current!.getBoundingClientRect().width / 100;
    const screen = frame.current!.parentElement!.parentElement!.clientWidth;
    return { ch, max: Math.floor(screen / ch) };
  };

  const onPointerDown = (side: "left" | "right") => (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.classList.add("resizing-measure");
    setDragging(side);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const rect = frame.current!.getBoundingClientRect();
    const half = Math.abs(e.clientX - (rect.left + rect.width / 2));
    const { ch, max } = fit();
    setPageWidth(Math.min((2 * half) / ch, max));
  };

  const onPointerUp = () => {
    document.body.classList.remove("resizing-measure");
    setDragging(null);
  };

  const onKeyDown = (side: "left" | "right") => (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 10 : 2;
    const outward = side === "right" ? "ArrowRight" : "ArrowLeft";
    const inward = side === "right" ? "ArrowLeft" : "ArrowRight";
    if (e.key === outward) setPageWidth(Math.min(width + step, fit().max));
    else if (e.key === inward) setPageWidth(width - step);
    else if (e.key === "Home") setPageWidth(PAGE_WIDTH.default);
    else return;
    e.preventDefault();
  };

  const handle = (side: "left" | "right") => (
    <div
      className={`measure-handle ${side}${dragging === side ? " dragging" : ""}`}
      role="separator"
      aria-orientation="vertical"
      aria-label="Page width"
      aria-valuemin={PAGE_WIDTH.min}
      aria-valuemax={PAGE_WIDTH.max}
      aria-valuenow={width}
      aria-valuetext={`${width} characters`}
      tabIndex={0}
      title="Drag to change the page width. Double-click to reset."
      onPointerDown={onPointerDown(side)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={() => setPageWidth(PAGE_WIDTH.default)}
      onKeyDown={onKeyDown(side)}
    />
  );

  return (
    <div className="measure-handles">
      <div className="measure-frame" ref={frame}>
        <div className="measure-probe" ref={probe} />
        {handle("left")}
        {handle("right")}
      </div>
    </div>
  );
}
